import React, {useMemo, useState} from 'react';
import {Button, DropdownList, FlexRow, SearchField, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {ConditionTile} from './ConditionTile';
import {isValueEditable, ReportTaxonomyField} from '../contracts/report-taxonomy.types';

/** Grid of active filter-condition tiles plus an "Add condition" trigger that opens a searchable,
 * context-grouped picker of the remaining value-editable taxonomy fields. Renders nothing while
 * `taxonomy` hasn't loaded — `addCondition`/`updateConditionValues`/`removeCondition` all no-op
 * without it anyway (see ReportBuilderContext), so there is nothing useful to render. */
export function ConditionsEditor(): JSX.Element | null {
	const {formatMessage} = useIntl();
	const {definition, taxonomy, addCondition, updateConditionValues, removeCondition} = useReportBuilder();
	const [search, setSearch] = useState('');

	const filterConditions = definition.filter.filterConditions;

	// Tracks how many conditions share each field so tiles can be keyed by field identity (stable
	// across additions/removals elsewhere in the list) while still falling back to a unique key if
	// the same field ever appears twice (addCondition's own de-dupe against `usedFilterFields` below
	// prevents that via this control, but a condition list built elsewhere — e.g. an AI patch —
	// isn't guaranteed to be unique).
	const fieldOccurrences = useMemo(() => {
		const counts = new Map<string, number>();
		for (const condition of filterConditions) {
			counts.set(condition.field, (counts.get(condition.field) ?? 0) + 1);
		}
		return counts;
	}, [filterConditions]);

	// Conditions key on the filterField enum (STATUS, PROJECT, ...), not the taxonomy `key`, so the
	// add-picker must exclude a field whose `filterField` already has a condition (comparing against
	// `field.key` would never match and let duplicates through).
	const usedFilterFields = useMemo(() => new Set(filterConditions.map(condition => condition.field)), [filterConditions]);

	const fieldsByContext = useMemo(() => {
		const grouped = new Map<string, ReportTaxonomyField[]>();
		if (!taxonomy) {
			return grouped;
		}
		const lowerSearch = search.trim().toLowerCase();
		for (const field of taxonomy.fields) {
			// A field with no `filterField` cannot be expressed as a filter condition (it would store
			// `field: null`, which crashes downstream label/summary code). Such fields — e.g. the
			// derived booleans "Over estimate"/"Overdue" — must never be offered in the add picker,
			// even though they are otherwise value-editable (boolean).
			if (!field.filterField || !isValueEditable(taxonomy, field) || usedFilterFields.has(field.filterField)) {
				continue;
			}
			if (lowerSearch && !field.label.toLowerCase().includes(lowerSearch)) {
				continue;
			}
			const group = grouped.get(field.context) ?? [];
			group.push(field);
			grouped.set(field.context, group);
		}
		return grouped;
	}, [taxonomy, usedFilterFields, search]);

	if (!taxonomy) {
		return null;
	}

	const hasAvailableFields = [...fieldsByContext.values()].some(fields => fields.length > 0);

	return (
		<FlexRow gap={'xl'} wrap={'wrap'} alignItems={'flex-start'} data-cy={'ai-report-conditions-editor'}>
			{filterConditions.map((condition, index) => {
				const key = (fieldOccurrences.get(condition.field) ?? 0) > 1 ? `${condition.field}-${index}` : condition.field;
				return (
					<ConditionTile
						key={key}
						condition={condition}
						index={index}
						taxonomy={taxonomy}
						onChangeValues={updateConditionValues}
						onRemove={removeCondition}
					/>
				);
			})}
			<DropdownList>
				<DropdownList.Trigger>
					{/* Children must be a plain string, not a `<FormattedMessage>` element: `BaseButton`
					 * derives its `aria-label` from `children?.toString()` when non-waiting, and a React
					 * element stringifies to '[object Object]', breaking the accessible name (see
					 * AIBanner's/ColumnManager's Buttons, which use the same `formatMessage(...)` pattern
					 * for this reason). */}
					<Button emphasis={'low'} icon={'plus'} data-cy={'ai-report-condition-add'}>
						{formatMessage({id: 'ai_report_builder.conditions.add_condition'})}
					</Button>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					<SearchField
						value={search}
						onChange={setSearch}
						isExpandable={false}
						placeholder={formatMessage({id: 'common.search'})}
						data-cy={'ai-report-condition-add-search'}
					/>
					{hasAvailableFields ? (
						taxonomy.contexts.map(context => {
							const fields = fieldsByContext.get(context.value);
							if (!fields || fields.length === 0) {
								return null;
							}
							return (
								<DropdownList.ContentGroup key={context.value} title={context.label}>
									{fields.map(field => (
										<DropdownList.Item
											key={field.key}
											onClick={() => addCondition(field.key)}
											data-cy={`ai-report-condition-add-option-${field.key}`}
										>
											{field.label}
										</DropdownList.Item>
									))}
								</DropdownList.ContentGroup>
							);
						})
					) : (
						<Text color={'medium'}>
							<FormattedMessage id={'ai_report_builder.conditions.add_empty'} />
						</Text>
					)}
				</DropdownList.Content>
			</DropdownList>
		</FlexRow>
	);
}
